import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Alert, AlertType } from './alerts.model';

@Injectable({ providedIn: 'root' })
export class AlertsService {
    private subject = new Subject<Alert>();
    private keepAfterRouteChange = false;
    private timeouts = new Map<string | undefined, ReturnType<typeof setTimeout>>();

    constructor(private router: Router) {
        // clear alert messages on route change unless 'keepAfterRouteChange' flag is true
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (this.keepAfterRouteChange) {
                    // only keep for a single route change
                    this.keepAfterRouteChange = false;
                } else {
                    // clear alert messages
                    this.clear();
                }
            }
        });
    }

    // enable subscribing to alerts observable
    onAlert(alertId?: string): Observable<Alert> {
        return this.subject.asObservable().pipe(filter(x => x && x.alertId === alertId));
    }

    // convenience methods
    success(message: string, alertId?: string) {
        this.alert(new Alert({ message, type: AlertType.Success, alertId }));
        this.timeout(alertId);
    }

    error(message: string, alertId?: string) {
        this.alert(new Alert({ message, type: AlertType.Error, alertId }));
        this.timeout(alertId);
    }

    info(message: string, alertId?: string) {
        this.alert(new Alert({ message, type: AlertType.Info, alertId }));
        this.timeout(alertId);
    }

    warning(message: string, alertId?: string) {
        this.alert(new Alert({ message, type: AlertType.Warning, alertId }));
        this.timeout(alertId);
    }

    // main alert method    
    alert(alert: Alert) {
        this.keepAfterRouteChange = alert.keepAfterRouteChange;
        this.subject.next(alert);
    }

    // clear alerts
    clear(alertId?: string) {
        const timeout = this.timeouts.get(alertId);
        if (timeout !== undefined) clearTimeout(timeout);
        this.timeouts.delete(alertId);
        this.subject.next(new Alert({ alertId }));
    }
    timeout(alertId?: string){
        const timeout = this.timeouts.get(alertId);
        if (timeout !== undefined) clearTimeout(timeout);
        this.timeouts.set(alertId, setTimeout(() => {
          this.clear(alertId);
        }, 10000));
      }
}
